import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import CommonModalUI from "@/components/ui/Modal/CommonModalUI";
import { Input } from "@/components/forms/Input";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";

export type TPermissionSelectionReviewModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  selectedIds: string[];
  selectionNames: Record<string, string>;
  onDeselect: (id: string) => void;
};

export const PermissionSelectionReviewModal: React.FC<
  TPermissionSelectionReviewModalProps
> = ({ open, onClose, title, selectedIds, selectionNames, onDeselect }) => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedItems = useMemo(
    () =>
      selectedIds.map((id) => ({
        id,
        name:
          selectionNames[id] ?? t("PERMISSION_ASSIGNMENT.UNNAMED_PERMISSION"),
      })),
    [selectedIds, selectionNames, t],
  );

  const filteredSelectedItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return selectedItems;
    return selectedItems.filter(({ name }) =>
      name.toLowerCase().includes(query),
    );
  }, [selectedItems, searchTerm]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    if (open && selectedIds.length === 0) {
      onClose();
    }
  }, [open, selectedIds.length, onClose]);

  return (
    <CommonModalUI
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      type="info"
      buttonCancel={onClose}
      cancelText={t("common:CLOSE")}
      id="permission-selection-review-modal"
      contentProps={{ className: "modal-body px-5 py-4" }}
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("PERMISSION_ASSIGNMENT.REVIEW_SEARCH_PLACEHOLDER")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="max-h-64 divide-y divide-border overflow-y-auto">
          {filteredSelectedItems.length > 0 ? (
            filteredSelectedItems.map(({ id, name }) => (
              <div
                key={`review-${id}`}
                className="flex items-center justify-between gap-4 py-2"
              >
                <Typography variant="p" className="text-xs text-foreground">
                  {name}
                </Typography>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onDeselect(id)}
                  className="h-6 w-6 p-0"
                  aria-label={t("PERMISSION_ASSIGNMENT.REMOVE_FROM_SELECTION", {
                    name,
                  })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))
          ) : (
            <Typography
              variant="p"
              affects="muted"
              className="py-4 text-center"
            >
              {t("PERMISSION_ASSIGNMENT.NO_MATCHING_SELECTION")}
            </Typography>
          )}
        </div>
      </div>
    </CommonModalUI>
  );
};
